import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionStore } from '../../stores/useSessionStore';
import { organizationsApi } from '../../services/api/organizations';
import { useTranslation } from '../../i18n';
import MiniRegisterForm from '../organisatie/MiniRegisterForm';
import '../../styles/organisatie.css';

/**
 * AcceptInvitePage — publieke route /invite/:token (Refs #778, plan 4 B1).
 *
 * Werkt zonder ingelogde gebruiker (gemount in App.tsx public-routes blok).
 * Drie paden:
 *   1. Niet ingelogd + nieuw account → MiniRegisterForm (e-mail vast)
 *   2. Niet ingelogd + bestaand account → "Log in" knop met return-url
 *   3. Ingelogd → Accepteren / Afwijzen knoppen (e-mail-binding op client)
 *
 * Auto-decline via ?action=decline (vanuit e-mail-link).
 *
 * Phase 7 (#842): heritage cream-page + gradient-hero + medaillon + pills.
 */

/** Vult `{naam}`-plekken in een vertaalde zin met vetgedrukte waarden. */
function boldParts(template: string, values: Record<string, string>): React.ReactNode[] {
  return template
    .split(/\{(\w+)\}/)
    .map((part, i) => (i % 2 ? <strong key={i}>{values[part]}</strong> : part));
}

interface InvitationView {
  id: string;
  email: string;
  role: 'admin' | 'member';
  billing_period: 'annual' | 'monthly';
  personal_message: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
    branding: unknown;
  };
  inviter_name: string;
  account_exists: boolean;
  expires_at: string;
}

const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const user = useSessionStore((s) => s.user);
  const { t } = useTranslation();

  const [invitation, setInvitation] = useState<InvitationView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [autoDeclineRan, setAutoDeclineRan] = useState(false);
  const autoDeclineTriggered = useRef(false);

  const autoDecline = params.get('action') === 'decline';

  // Fetch invitation by token
  useEffect(() => {
    if (!token) {
      setError(t('inviteTokenMissing'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await organizationsApi.getInvitationByToken(token);
        if (!cancelled) setInvitation(data as InvitationView);
      } catch (err) {
        if (!cancelled) {
          console.error('Load invitation failed:', err instanceof Error ? err.message : err);
          setError(t('inviteLoadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  async function handleAccept() {
    if (!invitation) return;
    if (!user) {
      // Niet ingelogd, met bestaand account → ga naar login en kom terug
      navigate(`/?next=/invite/${token}`);
      return;
    }
    if (user.email.toLowerCase() !== invitation.email.toLowerCase()) {
      setError(t('ogInviteWrongAccount', { invited: invitation.email, current: user.email }));
      return;
    }
    setSubmitting(true);
    try {
      await organizationsApi.acceptOrgInvitation(invitation.id);
      navigate(`/welkom-bij-organisatie?o=${encodeURIComponent(invitation.organization.slug)}`);
    } catch (err) {
      console.error('Accept invitation failed:', err instanceof Error ? err.message : err);
      setError(t('inviteAcceptFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecline() {
    if (!invitation) return;
    setSubmitting(true);
    try {
      await organizationsApi.declineOrgInvitation(invitation.id);
      navigate('/');
    } catch (err) {
      console.error('Decline invitation failed:', err instanceof Error ? err.message : err);
      setError(t('inviteDeclineFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  // Auto-decline path (?action=decline)
  useEffect(() => {
    if (
      autoDecline &&
      invitation &&
      user &&
      !autoDeclineTriggered.current &&
      user.email.toLowerCase() === invitation.email.toLowerCase()
    ) {
      autoDeclineTriggered.current = true;
      handleDecline().finally(() => setAutoDeclineRan(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDecline, invitation, user]);

  if (loading) {
    return (
      <div className="org-standalone-page">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t('ogInviteLoading')}
        </p>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="org-standalone-page">
        <div className="org-standalone-card org-standalone-card--invite" style={{ padding: 28 }}>
          <h1
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 800,
              color: 'var(--text-main)',
              marginBottom: 8,
            }}
          >
            {t('ogInviteUnavailable')}
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ruby)' }} role="alert">
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!invitation) return null;

  if (autoDecline && !autoDeclineRan && user) {
    return (
      <div className="org-standalone-page">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t('ogInviteDeclining')}
        </p>
      </div>
    );
  }

  const wrongEmail = user && user.email.toLowerCase() !== invitation.email.toLowerCase();
  const orgInitial = (invitation.organization.name || '').trim().charAt(0).toUpperCase() || 'P';

  return (
    <div className="org-standalone-page">
      <div className="org-standalone-card org-standalone-card--invite">
        <div className="org-standalone-hero">
          <div
            aria-hidden="true"
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(212,175,55,.15)',
              border: '2px solid var(--gold)',
              color: 'var(--gold)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px',
              fontSize: 24,
              fontWeight: 800,
              position: 'relative',
              zIndex: 1,
            }}
          >
            {orgInitial}
          </div>
          <div className="org-hero-eyebrow" style={{ position: 'relative', zIndex: 1 }}>
            {t('acceptInviteEyebrow') || 'Uitnodiging'}
          </div>
          <div
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 800,
              color: 'var(--cream)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {t('acceptInviteFrom').replace('{name}', invitation.inviter_name)}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: 'var(--gold)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {invitation.organization.name}
          </div>
        </div>

        <div style={{ padding: '22px 28px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 14,
              background: 'var(--bg-input)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)',
              padding: '10px 14px',
              marginBottom: 18,
            }}
          >
            <div>
              <div className="org-section-label" style={{ marginBottom: 0 }}>
                {t('roleLabel') || 'Rol'}
              </div>
              <div
                style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 700,
                  color: 'var(--accent)',
                }}
              >
                {invitation.role === 'admin' ? t('orgRoleAdmin') : t('orgRoleMember')}
              </div>
            </div>
            <div style={{ height: 24, width: 1, background: 'var(--border-subtle)' }} />
            <div>
              <div className="org-section-label" style={{ marginBottom: 0 }}>
                {t('licenseLabel') || 'Licentie'}
              </div>
              <div
                style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 700,
                  color: 'var(--text-main)',
                }}
              >
                {invitation.billing_period === 'annual' ? t('year') : t('calendarMonth')}
              </div>
            </div>
          </div>

          {invitation.personal_message && (
            <blockquote
              style={{
                borderLeft: '3px solid var(--gold)',
                paddingLeft: 12,
                fontStyle: 'italic',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 0 14px 0',
                textAlign: 'left',
              }}
            >
              &ldquo;{invitation.personal_message}&rdquo;
            </blockquote>
          )}

          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              marginBottom: 18,
            }}
          >
            {t('acceptInviteExplanation') ||
              'Door te accepteren krijg je toegang tot Pecktree-projecten en kun je samenwerken met andere leden.'}
          </p>

          {error && invitation && (
            <p
              role="alert"
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--ruby)',
                marginBottom: 14,
              }}
            >
              {error}
            </p>
          )}

          {!user && !invitation.account_exists ? (
            <MiniRegisterForm
              invitationToken={token!}
              email={invitation.email}
              onSuccess={handleAccept}
            />
          ) : !user && invitation.account_exists ? (
            <>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-muted)',
                  marginBottom: 14,
                }}
              >
                {boldParts(t('ogAccountExistsFor'), { email: invitation.email })}
              </p>
              <button
                type="button"
                className="org-btn-primary"
                style={{ width: '100%', padding: 12 }}
                onClick={() => navigate(`/?next=/invite/${token}`)}
              >
                {t('ogLogInToContinue')}
              </button>
            </>
          ) : wrongEmail ? (
            <>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--ruby)',
                  marginBottom: 14,
                }}
              >
                {boldParts(t('ogInviteIsFor'), { invited: invitation.email, current: user!.email })}
              </p>
              <button
                type="button"
                onClick={() => navigate('/account')}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-lg)',
                  color: 'var(--text-muted)',
                  padding: '10px 14px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  font: 'inherit',
                  width: '100%',
                }}
              >
                {t('ogLogOutAndRetry')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="org-btn-primary"
                style={{ width: '100%', padding: 12, marginBottom: 6 }}
                onClick={handleAccept}
                disabled={submitting}
              >
                {submitting
                  ? t('accepting') || 'Accepteren...'
                  : t('acceptInviteAccept') || 'Uitnodiging accepteren'}
              </button>
              <button
                type="button"
                onClick={handleDecline}
                disabled={submitting}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  padding: 8,
                  fontSize: 'var(--text-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  font: 'inherit',
                  width: '100%',
                }}
              >
                {t('acceptInviteDecline') || 'Niet nu'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
